import { isEmpty } from 'lodash';

import { ActionVisibilityValueType } from 'utils/adl-services';
import findQuestionByRefId from 'utils/findQuestionByRefId';
import {
  SURVEY_TYPE_REGULAR,
  SURVEY_TYPE_THIRD_PARTY,
  SURVEY_TYPE_INTERNAL,
  SURVEY_TYPE_DISTRIBUTION_LINK,
  SURVEY_TYPE_PEEQ,
  SURVEY_CONFIG_PRICING_TYPE_FIXED_PRICING,
  SURVEY_CONFIG_PRICING_TYPE_REAL_TIME,
  SURVEY_CONFIG_PRICING_TYPE_NO_PRICING,
} from 'containers/Survey/constants';

import {
  USER_RESEARCHER,
  USER_ADMIN,
  USER_PUBLISHER,
} from 'containers/User/constants';

import { surveyStatusEnum } from 'containers/SurveysPage/constants';
import {
  ALLOWED_UNCONDITIONAL_HIDE_ANSWER_NODE_TYPES,
  ALLOWED_UNCONDITIONAL_HIDE_ROW_NODE_TYPES,
  ALLOWED_UNCONDITIONAL_HIDE_COLUMN_NODE_TYPES,
  GROUP_CONJOINT,
  GROUP_BASIC,
} from 'data/nodeTypes';
import getViewCheckpointId from 'utils/getViewCheckpointId';
import { canShowHideAnswers } from 'components/VisibilityActionButton/utils';
import { isGroup } from 'utils/nodeTypes';

// generic combination helpers (re-exported from utils/combinators)
import { and, or, not, always, never } from 'utils/combinators';
export { and, or, not, always, never };

// window helpers
export const isViewCheckpointMode = () => !!getViewCheckpointId();

// user helpers
export const isUserAdmin = ({ userType }) => userType === USER_ADMIN;
export const isUserResearcher = ({ userType }) => userType === USER_RESEARCHER;
export const isUserPublisher = ({ userType }) => userType === USER_PUBLISHER;
export const isUserGuest = ({ isGuest }) => isGuest;
export const isUserProdege = ({ isProdege }) => isProdege;

// survey state helpers
export const isStateUnderEdit = ({ surveyStatus }) =>
  surveyStatusEnum.UNDER_EDIT === surveyStatus;
export const isStateUnderApproval = ({ surveyStatus }) =>
  surveyStatusEnum.UNDER_APPROVAL === surveyStatus;
export const isStateApproved = ({ surveyStatus }) =>
  surveyStatusEnum.APPROVED === surveyStatus;
export const isStateCompleted = ({ surveyStatus }) =>
  surveyStatusEnum.COMPLETED === surveyStatus;
export const isStateRunning = ({ surveyStatus }) =>
  surveyStatusEnum.RUNNING === surveyStatus;
export const isStateDraft = ({ surveyStatus }) =>
  surveyStatusEnum.DRAFT === surveyStatus;
export const isStatePaused = ({ surveyStatus }) =>
  surveyStatusEnum.PAUSED === surveyStatus;
export const isStatePausedCpi = ({ surveyStatus }) =>
  surveyStatusEnum.PAUSED_REVIEW_CPI === surveyStatus;
export const isStatePausedUnderCompletion = ({ surveyStatus }) =>
  surveyStatusEnum.PAUSED_UNDER_COMPLETION === surveyStatus;
export const isStatePausedUnderProbation = ({ surveyStatus }) =>
  surveyStatusEnum.PAUSED_UNDER_PROBATION === surveyStatus;
export const isStatePausedQuotasFillingUp = ({ surveyStatus }) =>
  surveyStatusEnum.PAUSED_QUOTAS_FILLING_UP === surveyStatus;
export const isStateCancelled = ({ surveyStatus }) =>
  surveyStatusEnum.CANCELLED === surveyStatus;
export const isStateFailed = ({ surveyStatus }) =>
  surveyStatusEnum.FAILED === surveyStatus;
export const isStatePendingToStart = ({ surveyStatus }) =>
  surveyStatusEnum.PENDING_TO_START === surveyStatus;
export const isStatePendingToComplete = ({ surveyStatus }) =>
  surveyStatusEnum.PENDING_COMPLETE === surveyStatus;
export const isStateInvoiced = ({ surveyStatus }) =>
  surveyStatusEnum.INVOICED === surveyStatus;

// survey type helpers
export const isSurveyTypeDistributionLink = ({ surveyType }) =>
  surveyType === SURVEY_TYPE_DISTRIBUTION_LINK;
export const isSurveyTypeRegular = ({ surveyType }) =>
  surveyType === SURVEY_TYPE_REGULAR;
export const isSurveyTypeThirdParty = ({ surveyType }) =>
  surveyType === SURVEY_TYPE_THIRD_PARTY;
export const isSurveyTypeInternal = ({ surveyType }) =>
  surveyType === SURVEY_TYPE_INTERNAL;
export const isSurveyTypePeeq = ({ surveyType }) =>
  surveyType === SURVEY_TYPE_PEEQ;

// perk helpers
export const hasPerkDisqualificationRules = ({ perks }) =>
  perks.disqualificationRulesPerk === true;

export const hasPerkSurveyTheming = ({ perks }) =>
  perks.surveyThemePerk === true;

export const hasPerkAdvancedSurveyTheming = ({ perks }) =>
  perks.advancedThemeSettingsPerk === true;

export const hasPerkThirdParty = ({ perks }) =>
  perks.pfThirdPartySurvey === true && perks.regularSurvey === true;

export const hasPerkDistributionLink = ({ perks }) =>
  perks.distributionLinkPerk === true;

export const hasPerkRegular = ({ perks }) => perks.regularSurvey === true;

export const hasPerkTranslations = ({ perks }) => perks.translations === true;

export const hasPerkHiddenVariables = ({ perks }) =>
  perks.hiddenVariables === true;

export const hasPerkLoopAndMerge = ({ perks }) => perks.loopAndMerge === true;

export const hasPerkEditLiveQuestionnaire = ({ perks }) =>
  perks.editLiveQuestionnaire === true;

export const hasPerkTemplates = ({ perks }) =>
  perks.useSurveyTemplates === true;

export const hasPerkScheduling = ({ perks }) => perks.scheduling === true;
export const hasPerkConversational = ({ perks }) =>
  perks.conversationalAiPerk === true;

export const hasPerkQuotasSimple = ({ perks }) =>
  perks.questionnaireSimpleQuotas === true;

export const hasPerkQuotasAdvanced = ({ perks }) =>
  perks.questionnaireAdvancedQuotas === true;

export const hasPerkMinimumResponses = ({ perks }) =>
  perks.questionnaireMinimumResponses === true;

// survey feature helpers
export const isSurveyRecurring = ({ surveyFeatures = {} }) =>
  surveyFeatures.surveyIsRecurring === true;

export const isSurveyScheduled = ({ surveyFeatures = {} }) =>
  surveyFeatures.surveyIsScheduled === true;

// survey config helpers
export const isSurveyConfigManagedTargeting = ({ surveyConfig = {} }) =>
  surveyConfig.managedTargeting === true;

export const isSurveyConfigWithFraudChecks = ({ surveyConfig = {} }) =>
  surveyConfig.supportsFraudChecks === true;

export const isSurveyConfigPricingFixed = ({ surveyConfig }) =>
  surveyConfig.pricingType === SURVEY_CONFIG_PRICING_TYPE_FIXED_PRICING;

export const isSurveyConfigPricingRealTime = ({ surveyConfig }) =>
  surveyConfig.pricingType === SURVEY_CONFIG_PRICING_TYPE_REAL_TIME;

export const isSurveyConfigNoPricing = ({ surveyConfig = {} }) =>
  surveyConfig.pricingType === SURVEY_CONFIG_PRICING_TYPE_NO_PRICING;

export const isSurveyConfigMultiCountries = ({ surveyConfig = {} }) =>
  surveyConfig.allowMultipleCountries === true;

// questionnaire revisions undo/redo
export const isQuestionnaireInRevisionPreviewMode = ({
  isInRevisionPreviewMode,
}) => isInRevisionPreviewMode;

// ai builder
export const isAiBuilderIdle = ({ isAiIdle }) => isAiIdle;

export const hasHiddenVariablesData = ({ hiddenVariables }) =>
  hiddenVariables.length > 0;

export const isFinalisedUnderEdit = ({ isFinalisedUnderEdit: isFinalised }) =>
  isFinalised;

export const isRandomizedQuestionnaire = ({
  isRandomizedQuestionnaire: isRandomized,
}) => isRandomized;

export const isScreeningNode = ({ itemToCheck }) =>
  itemToCheck?.isScreening === true;

// Checks performed based on dynamic data
export const isItemFromCurrentRevision = ({ itemToCheck }) =>
  itemToCheck?.id === undefined || itemToCheck?.id === null;

export const isConjointGroup = ({ itemToCheck }) =>
  itemToCheck?.type === GROUP_CONJOINT ||
  itemToCheck?.node?.type === GROUP_CONJOINT;

export const isConjointGroupFromCurrentRevision = ({ itemToCheck }) =>
  isConjointGroup({ itemToCheck }) &&
  isItemFromCurrentRevision({ itemToCheck: itemToCheck?.node || itemToCheck });

export const hasQuestionnaireCarryForward = ({ nodes = [] }) =>
  nodes.some(({ carryForward = [] }) => carryForward.length > 0);

export const hasQuestionnaireLoopAndMergeFromQuestion = ({ nodes = [] }) =>
  nodes.some((node) => {
    const { loopAndMergeFromQuestionConfiguration = {} } = node;
    return (
      !isEmpty(loopAndMergeFromQuestionConfiguration) &&
      !loopAndMergeFromQuestionConfiguration.isProfiling
    );
  });

export const isItemInsideGroup = ({ itemToCheck }) =>
  (itemToCheck?.groupRefId !== undefined && itemToCheck?.groupRefId !== null) ||
  (itemToCheck?.node?.groupRefId !== undefined &&
    itemToCheck?.node?.groupRefId !== null);

export const hasUnconditionalHideAnswerLogic = ({
  answersWithLogicFromOutcomesHide = [],
  itemToCheck,
}) => answersWithLogicFromOutcomesHide.includes(itemToCheck?.refId);

export const isAllowedUnconditionalHideAnswerLogicOnNode = ({
  itemToCheck,
  nodes = [],
}) => {
  if (!itemToCheck?.questionRefId) {
    return false;
  }
  const node = findQuestionByRefId(nodes, itemToCheck.questionRefId);

  if (!node || node.isScreening) {
    return false;
  }

  switch (itemToCheck?.valueType) {
    case ActionVisibilityValueType.Answer:
      return ALLOWED_UNCONDITIONAL_HIDE_ANSWER_NODE_TYPES.includes(node.type);
    case ActionVisibilityValueType.Row:
      return ALLOWED_UNCONDITIONAL_HIDE_ROW_NODE_TYPES.includes(node.type);
    case ActionVisibilityValueType.Column:
      return ALLOWED_UNCONDITIONAL_HIDE_COLUMN_NODE_TYPES.includes(node.type);
    default:
      return false;
  }
};

export const itemIsUsedInCarryForwardFromPreviousRevision = ({
  nodes = [],
  itemToCheck,
}) => {
  if (!itemToCheck?.refId) {
    return false;
  }

  return nodes.some((node) => {
    const { carryForward = [] } = node;
    return carryForward.some(
      (config) =>
        config.questionRefId === itemToCheck.refId &&
        !isItemFromCurrentRevision({ itemToCheck: config }),
    );
  });
};

export const itemHasCarryForwardFromPreviousRevision = ({ itemToCheck }) => {
  const { carryForward = [] } = itemToCheck || {};
  return carryForward.some(
    (carryForwardConfig) =>
      !isItemFromCurrentRevision({ itemToCheck: carryForwardConfig }),
  );
};

export const belongsToVanWestendorpGroup = ({ itemToCheck }) =>
  itemToCheck?.inVanWestendorpGroup === true;

export const isGroupNode = ({ itemToCheck }) => isGroup(itemToCheck);

export const isHiddenNodeByLogic = ({ hiddenNodes = [], itemToCheck }) =>
  hiddenNodes.includes(itemToCheck?.refId);

export const isCustomTargetingNode = ({ itemToCheck }) =>
  itemToCheck?.isCustomTargetingNode === true;

export const canShowDisplayLogicOnAnswers = ({ itemToCheck }) =>
  canShowHideAnswers(itemToCheck);

export const canShowDisplayLogicOnRows = ({ itemToCheck }) =>
  canShowHideAnswers({
    ...itemToCheck,
    valueType: ActionVisibilityValueType.Row,
  });

export const canShowDisplayLogicOnColumns = ({ itemToCheck }) =>
  canShowHideAnswers({
    ...itemToCheck,
    valueType: ActionVisibilityValueType.Column,
  });

export const groupHasQuestionFromPreviousRevision = ({ itemToCheck }) =>
  itemToCheck?.questions?.some(
    (question) => !isItemFromCurrentRevision({ itemToCheck: question }),
  );

export const isItemInsideBasicGroupNode = ({ nodes = [], itemToCheck }) => {
  if (!isItemInsideGroup({ itemToCheck })) {
    return false;
  }
  const groupRefId = itemToCheck?.groupRefId || itemToCheck?.node?.groupRefId;
  if (!groupRefId) {
    return false;
  }

  const group = nodes.find((node) => node.refId === groupRefId);
  if (!group) {
    return false;
  }

  return group.type === GROUP_BASIC;
};

export const hasPermissionQuestionReorderMoveInsideGroup = and(
  not(isScreeningNode),
  not(itemHasCarryForwardFromPreviousRevision),
  not(itemIsUsedInCarryForwardFromPreviousRevision),
  or(
    isItemInsideBasicGroupNode,
    and(not(isItemInsideBasicGroupNode), not(isItemInsideGroup)),
  ),
);

export const canMoveNodeAfterNode = ({ nodes = [], itemToCheck }) => {
  const { nodeToCheck, ...node } = itemToCheck;
  const nodeInsideGroup = isItemInsideGroup({ itemToCheck: nodeToCheck });

  if (
    nodeInsideGroup &&
    !hasPermissionQuestionReorderMoveInsideGroup({
      nodes,
      itemToCheck: nodeToCheck,
    })
  ) {
    return false;
  }

  const isAllowedToMoveByCarryForward =
    !itemHasCarryForwardFromPreviousRevision({ itemToCheck: node }) &&
    !itemIsUsedInCarryForwardFromPreviousRevision({ nodes, itemToCheck: node });

  if (isAllowedToMoveByCarryForward) {
    return true;
  }

  if (!nodeInsideGroup) {
    return true;
  }

  return false;
};
